import ButtonBase from "@mui/material/ButtonBase";
import Grid from "components/primitives/Grid";
import useAthlete from "hooks/useAthlete";
import useUnitFormatter from "hooks/useUnitFormatter";
import AthleteName from "components/AthleteName";
import { useNavigate } from "react-router-dom";
import AnalyticsService from "services/AnalyticsService";
import { CircuitMetric, ExerciseMetric, FireStoreMeasurement, ScoreMetadata } from "types";
import logEvent from "utils/logEvent";
import * as LeaderboardsComponents from "../Leaderboards.components";
import usePreferences from "hooks/usePreferences";
import UnitHelper from "helpers/UnitHelper";

export const LeaderboardTableAbsoluteRow = (props: {
  position: number | null;
  measurement: FireStoreMeasurement;
  metric?: ExerciseMetric | CircuitMetric;
  scoreMetadata?: ScoreMetadata;
}) => {
  const { position, measurement, metric, scoreMetadata } = props;
  const athlete = useAthlete(measurement.athleteId, true);
  const formatUnit = useUnitFormatter();
  const navigate = useNavigate();
  const [preferences] = usePreferences();

  const clickableProps = athlete && {
    component: ButtonBase,
    onClick: () => {
      logEvent("HUB_LIVE_ATHLETE_PROFILE_CHART_EXERCISE", {
        [AnalyticsService.PROPERTIES.EXERCISE_ID]: measurement.exerciseId,
        [AnalyticsService.PROPERTIES.EVENT_CLICK_LOCATION]: "liveLeaderboard",
      });
      navigate(`/athlete-hub/${athlete.id}?chartType=LINE&metadataId=${measurement.exerciseId}`);
    },
  };

  // For Score type leaderboards, use the score field directly
  const metricValue = metric ? measurement[metric.field] : measurement.score;

  // Format the value based on whether it's a metric or score
  const formattedValue = (() => {
    if (metric) {
      return formatUnit(metricValue, metric);
    }

    if (scoreMetadata?.displayMetadata) {
      // Format score using displayMetadata with unit conversion
      const displayMeta = preferences.imperialSystemEnabled
        ? scoreMetadata.displayMetadata.imperial
        : scoreMetadata.displayMetadata.metric;

      if (displayMeta && typeof metricValue === "number") {
        // Use UnitHelper to apply unit conversion (score * 100) and formatting
        return UnitHelper.process(metricValue, {
          from: scoreMetadata.unitOfMeasure,
          to: displayMeta.unit,
          decimals: displayMeta.decimals,
        });
      }
    }

    return metricValue;
  })();

  return (
    <LeaderboardsComponents.TableRowStyledBox {...clickableProps}>
      <Grid container alignItems="center" p={0} spacing="0px">
        <LeaderboardsComponents.PositionCellGrid>{position}</LeaderboardsComponents.PositionCellGrid>
        <Grid size="grow">
          <AthleteName athlete={athlete} />
        </Grid>
        <LeaderboardsComponents.MetricCellGrid>{formattedValue}</LeaderboardsComponents.MetricCellGrid>
      </Grid>
    </LeaderboardsComponents.TableRowStyledBox>
  );
};
